import {
    Button,
    DrawerProps,
    Group,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import {PlatformDrawer} from '@flexinet/ui-components';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    PaymentIntentsFilters,
    usePaymentIntentsFilter,
} from '../hooks/usePaymentIntentsFilter';
import {PaymentIntentStatus} from '../types';

/**
 * FNP-918: the payment-intents filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (usePaymentIntentsFilter), not component state, so the
 * table — a sibling, not a child — filters the same list.
 *
 * ═══ STATUS IS FILTERED CLIENT-SIDE, AND THE DRAWER SAYS SO ═══
 *
 * `PaymentIntentsFilterInput` declares only `{customerId: String}` — there is
 * no `status` argument. Sending one would not error: AppSync silently DROPS
 * undeclared input fields, so the request would return the unfiltered list
 * while the badge showed an active filter. So `status` is matched over the
 * pages loaded so far, and the note below tells the operator that, because a
 * count they cannot trust is worse than a count they can qualify.
 *
 * `customerId` is NOT offered here even though the API supports it: it is
 * already the table's scope (the customer EditPage tab passes it as a prop),
 * and the keyword search covers it on the index page. Offering a second, weaker
 * way to set the same thing is the kind of surface §7 asks us not to build.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = usePaymentIntentsFilter();

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Payment Intents')}
            size='md'
            {...rest}
        >
            <Formik<PaymentIntentsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Status')}
                                placeholder={I18n.get('Any')}
                                clearable
                                // Every status the schema's PaymentIntentStatus
                                // enum declares — this list is short and closed,
                                // so all of it is offered rather than a subset.
                                data={Object.values(PaymentIntentStatus)
                                            .map(status => ({
                                                value: status,
                                                label: I18n.get(status),
                                            }))}
                                value={values.status ?? null}
                                onChange={(value) =>
                                    setFieldValue('status', value ?? '')}
                            />

                            {/*
                              * Says out loud what the filter can and cannot do.
                              * The API takes no status argument, so this narrows
                              * the rows already fetched — an operator who reads
                              * the result as a dataset-wide count would be wrong.
                              */}
                            <Text size='xs' c='dimmed'>
                                {I18n.get(
                                    'Status is filtered across the payment intents loaded '
                                    + 'so far. Load more rows to widen the search.',
                                )}
                            </Text>

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
